/*
* === SearchApplicationsPage.js (Final - Cleaned & Full Detailed Excel Export) ===
*/
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
    Container, Typography, Card, Box, Alert, IconButton, TextField, Button, Grid, Paper, 
    FormControl, InputLabel, Select, Dialog, DialogTitle, DialogContent, DialogActions, 
    CircularProgress, MenuItem, Checkbox, ListItemText, Chip, Link, Snackbar 
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Papa from 'papaparse'; 
import { saveAs } from 'file-saver'; 
import * as XLSX from 'xlsx'; 
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';

import SearchIcon from '@mui/icons-material/Search';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import EmailIcon from '@mui/icons-material/Email';
import RuleIcon from '@mui/icons-material/Rule';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { tenantConfig } from '../../config/tenantConfig';

// --- AssignReviewerModal ---
const AssignReviewerModal = ({ open, onClose, application, token, onAssign, showToast }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [reviewers, setReviewers] = useState([]);
    const [selectedReviewer, setSelectedReviewer] = useState(application?.ReviewerId || '');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setLoading(true);
            api.get('/admin/users', { headers: { Authorization: `Bearer ${token}` } })
                .then(res => {
                    const eligible = res.data.filter(u => ['Admin', 'Reviewer', 'SuperUser'].includes(u.Role) && u.IsActive);
                    setReviewers(eligible);
                    setSelectedReviewer(application?.ReviewerId || '');
                })
                .catch(err => console.error(err))
                .finally(() => setLoading(false));
        }
    }, [open, token, application]);

    const handleAssign = async () => {
        try {
            await api.post(`/admin/applications/${application.Id}/assign`, { assignedToId: selectedReviewer }, { headers: { Authorization: `Bearer ${token}` } });
            showToast('Reviewer assigned successfully.', 'success');
            onAssign(); 
            onClose();
        } catch (e) { 
            showToast('Assignment failed.', 'error'); 
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIndIcon color="primary" /> Assign Reviewer
            </DialogTitle>
            <DialogContent>
                {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box> : (
                    <>
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            Select a team member to review Application #{application?.Id}.
                        </Typography>

                        <FormControl fullWidth margin="dense" size="small">
                            <InputLabel>Select Reviewer</InputLabel>
                            <Select value={selectedReviewer} label="Select Reviewer" onChange={(e) => setSelectedReviewer(e.target.value)}>
                                <MenuItem value=""><em>Unassigned</em></MenuItem>
                                {reviewers.map(r => (
                                    <MenuItem key={r.Id} value={r.Id}>
                                        {r.FirstNames} {r.Surname} {r.Id === user.id ? '(Me)' : ''} ({r.Role})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {reviewers.length <= 1 && (
                            <Alert severity="info" sx={{ mt: 2, fontSize: '0.85rem' }}>
                                You are currently the only available reviewer.
                                <Box sx={{ mt: 1 }}>
                                    <Link 
                                        component="button" 
                                        variant="body2" 
                                        onClick={() => { onClose(); navigate('/admin/users'); }}
                                        sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                                    >
                                        Invite new users here
                                    </Link>
                                </Box>
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleAssign} variant="contained" disabled={loading}>Assign</Button>
            </DialogActions>
        </Dialog>
    );
};

// --- MODAL COMPONENT: Request Documents Modal ---
const RequestDocumentsModal = ({ open, onClose, application, token, onUpdated, showToast }) => {
    const [selectedDocs, setSelectedDocs] = useState([]);
    const [adminNote, setAdminNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const standardDocOptions = [
        'ID / Passport Document',
        'Proof of Residential Address',
        'Certified Payslip / Proof of Household Income',
        'Latest Academic Results / Matric Certificate',
        'Proof of University Registration / Acceptance Letter',
        'Proof of Banking / Bank Confirmation Letter',
        'NUM Membership Proof (JB Marks)',
        'Affidavit (Unemployed Parent / Guardian)'
    ];

    useEffect(() => {
        if (open) {
            setSelectedDocs([]);
            setAdminNote('');
        }
    }, [open]);

    const handleToggleDoc = (docName) => {
        setSelectedDocs(prev => 
            prev.includes(docName) ? prev.filter(d => d !== docName) : [...prev, docName]
        );
    };

    const handleSendRequest = async () => {
        if (selectedDocs.length === 0) {
            showToast('Please select at least one document to request.', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            await api.post(`/admin/applications/${application.Id}/request-documents`, {
                requestedDocs: selectedDocs,
                adminNote
            }, { headers: { Authorization: `Bearer ${token}` } });

            showToast(`Document request sent to ${application.FullName || 'applicant'}.`, 'success');
            onUpdated();
            onClose();
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to send document request.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <UploadFileIcon color="warning" /> Request Updated Documents
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select the missing or uncertified documents required for <strong>{application?.FullName}</strong> (Ref: {application?.ApplicationRef || application?.Id}):
                </Typography>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                    {standardDocOptions.map((docName) => (
                        <Grid item xs={12} sm={6} key={docName}>
                            <Box 
                                onClick={() => handleToggleDoc(docName)}
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    p: 1, 
                                    border: '1px solid',
                                    borderColor: selectedDocs.includes(docName) ? 'primary.main' : 'divider',
                                    borderRadius: 1,
                                    bgcolor: selectedDocs.includes(docName) ? 'action.selected' : 'background.paper',
                                    cursor: 'pointer',
                                    transition: '0.2s'
                                }}
                            >
                                <Checkbox checked={selectedDocs.includes(docName)} size="small" sx={{ p: 0.5, mr: 1 }} />
                                <Typography variant="caption" fontWeight={selectedDocs.includes(docName) ? 'bold' : 'normal'}>
                                    {docName}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <TextField 
                    label="Reviewer Instructions / Rejection Reason"
                    fullWidth 
                    multiline 
                    rows={3} 
                    size="small" 
                    placeholder="e.g. Please provide a recently certified copy of your parent's payslip (not older than 3 months)."
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button 
                    onClick={handleSendRequest} 
                    variant="contained" 
                    color="warning" 
                    disabled={submitting || selectedDocs.length === 0}
                >
                    {submitting ? <CircularProgress size={20} /> : `Send Request (${selectedDocs.length})`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- Bulk Status Update Modal ---
const BulkStatusUpdateModal = ({ open, onClose, selectedIds, token, onUpdate, showToast }) => {
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);

    const handleUpdate = async () => {
        setLoading(true);
        try {
            await api.post('/admin/applications/bulk-update-status', { applicationIds: selectedIds, newStatus: status }, { headers: { Authorization: `Bearer ${token}` } });
            showToast('Status updated successfully.', 'success');
            onUpdate(); onClose();
        } catch (e) { showToast('Update failed', 'error'); } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Bulk Status Change</DialogTitle>
            <DialogContent sx={{ width: 400 }}>
                <Typography gutterBottom>Updating {selectedIds.length} applications.</Typography>
                <FormControl fullWidth margin="dense">
                    <InputLabel>New Status</InputLabel>
                    <Select value={status} label="New Status" onChange={(e) => setStatus(e.target.value)}>
                        <MenuItem value="All">All Statuses</MenuItem>
                        <MenuItem value="Submitted">Submitted</MenuItem>
                        <MenuItem value="Under Review">Under Review</MenuItem>
                        <MenuItem value="Shortlisted">Shortlisted</MenuItem>
                        <MenuItem value="Interview">Interview</MenuItem>
                        <MenuItem value="Approved">Approved</MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                        <MenuItem value="Funded">Funded</MenuItem>
                        <MenuItem value="Withdrawn">Withdrawn</MenuItem>
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Cancel</Button><Button onClick={handleUpdate} variant="contained" disabled={!status || loading}>Update</Button></DialogActions>
        </Dialog>
    );
};

// --- Bulk Email Modal ---
const BulkSendEmailModal = ({ open, onClose, selectedIds, token, onUpdate, showToast }) => {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedTemplateId(''); 
            api.get('/admin/templates', { headers: { Authorization: `Bearer ${token}` } })
               .then(res => setTemplates(res.data))
               .catch(err => showToast('Failed to load templates.', 'error'));
        }
    }, [open, token, showToast]);

    const handleSend = async () => {
        setLoading(true);
        try {
            await api.post('/admin/applications/bulk-send-email', { 
                applicationIds: selectedIds, 
                templateId: parseInt(selectedTemplateId, 10) 
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            showToast('Emails queued successfully.', 'success'); 
            onUpdate(); 
            onClose();
        } catch (e) { 
            showToast('Failed to send emails.', 'error'); 
        } finally { 
            setLoading(false); 
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Bulk Email</DialogTitle>
            <DialogContent sx={{ width: 400 }}>
                <Typography gutterBottom>Sending to {selectedIds.length} applicants.</Typography>
                <FormControl fullWidth margin="dense">
                    <InputLabel>Select Template</InputLabel>
                    <Select 
                        value={String(selectedTemplateId)} 
                        label="Select Template" 
                        onChange={(e) => setSelectedTemplateId(String(e.target.value))}
                    >
                        {templates.map((t, index) => {
                            const safeId = String(t.Id || t.id || `fallback-${index}`);
                            const tag = t.CompanyId ? '(Company)' : '(Global)';
                            
                            return (
                                <MenuItem key={safeId} value={safeId}>
                                    {t.TemplateName} {tag}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSend} variant="contained" disabled={!selectedTemplateId || loading}>Send</Button>
            </DialogActions>
        </Dialog>
    );
};

const SearchApplicationsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams(); 
    
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { token, user } = useAuth();

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [searchText, setSearchText] = useState('');
    const [selectionModel, setSelectionModel] = useState([]);
    
    // Modal States
    const [selectedApp, setSelectedApp] = useState(null);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isBulkStatusOpen, setIsBulkStatusOpen] = useState(false);
    const [isBulkEmailOpen, setIsBulkEmailOpen] = useState(false);
    const [isRequestDocsOpen, setIsRequestDocsOpen] = useState(false);

    const showToast = useCallback((msg, severity = 'success') => {
        setSnackbar({ open: true, message: msg, severity });
    }, []);

    const currentStatus = searchParams.get('status') || 'All';

    const fetchApplications = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const params = {};
            if (searchText) params.search = searchText;
            
            const activeStatus = searchParams.get('status');
            if (activeStatus && activeStatus !== 'All') params.status = activeStatus;

            const response = await api.get('/admin/applications', { 
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            setApplications(response.data);
        } catch (err) {
            setError('Failed to load applications.');
        } finally {
            setLoading(false);
        }
    }, [token, searchText, searchParams]);

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    const handleStatusFilterChange = (e) => {
        const newStatus = e.target.value;
        const newParams = new URLSearchParams(searchParams);
        
        if (newStatus && newStatus !== 'All') {
            newParams.set('status', newStatus);
        } else {
            newParams.delete('status');
        }
        setSearchParams(newParams);
    };

    const filteredRows = useMemo(() => {
        let result = applications;
        const urlFilter = searchParams.get('filter');
        if (urlFilter === 'assigned_to_me') {
            result = result.filter(app => String(app.ReviewerId) === String(user?.id));
        }
        return result;
    }, [applications, searchParams, user]);

    const handleCloseModals = () => { 
        setIsAssignModalOpen(false); 
        setIsBulkStatusOpen(false); 
        setIsBulkEmailOpen(false); 
        setIsRequestDocsOpen(false);
        setSelectedApp(null); 
    };

    const handleBankExport = async () => {
        try {
            const res = await api.get('/admin/finance/batch-payment-export', { headers: { Authorization: `Bearer ${token}` } });
            const csv = Papa.unparse(res.data);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, `Bank_Batch_Export_${new Date().toISOString().split('T')[0]}.csv`);
            showToast("Batch file generated for verified accounts.", "success");
        } catch (e) {
            showToast("No verified applicants found to export.", "warning");
        }
    };

    const handleExcelExport = async () => {
        const targetIds = (selectionModel && selectionModel.length > 0)
            ? selectionModel
            : filteredRows.map(app => app.Id);

        if (!targetIds || targetIds.length === 0) {
            showToast("No records available or selected for export.", "warning");
            return;
        }

        showToast(`Preparing detailed Excel file for ${targetIds.length} record(s)...`, "info");

        try {
            const parseJson = (data) => {
                if (!data) return {};
                if (typeof data === 'object') return data;
                try {
                    const parsed = JSON.parse(data);
                    return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
                } catch (e) {
                    return {};
                }
            };

            const detailedRecordsPromises = targetIds.map(async (id) => {
                try {
                    const res = await api.get(`/admin/applications/${id}`, { 
                        headers: { Authorization: `Bearer ${token}` } 
                    });
                    return res.data;
                } catch (err) {
                    return applications.find(app => app.Id === id);
                }
            });

            const fullApplications = await Promise.all(detailedRecordsPromises);

            const exportData = fullApplications.map(app => {
                const personal = parseJson(app.PersonalDetails);
                const academic = parseJson(app.AcademicInfo);
                const financialInfo = parseJson(app.FinancialInfo);
                const financial = financialInfo.financial || financialInfo;
                const guardianJson = financialInfo.guardian || {};
                const guardianDb = app.guardianRecord || {};
                const jbmarks = parseJson(app.JBMarksData);
                const address = personal.address || {};

                let subjectsSummary = 'N/A';
                if (Array.isArray(app.academicResults) && app.academicResults.length > 0) {
                    subjectsSummary = app.academicResults
                        .map(r => `${r.Subject}: ${r.Grade || r.Mark}%`)
                        .join('; ');
                } else if (academic.subjects) {
                    subjectsSummary = Object.entries(academic.subjects)
                        .filter(([_, data]) => data.selected)
                        .map(([key, data]) => `${key.replace(/([A-Z])/g, ' $1').trim()}: ${data.level || 'Selected'}`)
                        .join('; ');
                }

                let statusReason = app.StatusReason || app.Feedback || '';
                if (!statusReason || statusReason.trim() === '') {
                    if (app.ReinstateReason) {
                        statusReason = `Reinstated: ${app.ReinstateReason}`;
                    } else {
                        statusReason = 'No status reason recorded';
                    }
                }

                const firstNames = app.MasterFirstNames || personal.FirstNames || personal.firstNames || personal.firstName || 'N/A';
                const surname = app.MasterSurname || personal.Surname || personal.surname || 'N/A';
                const cellNumber = app.MasterCellNumber || personal.CellNumber || personal.cellNumber || personal.cellNo || 'N/A';
                const idNumber = app.MasterIDNumber || personal.IDNumber || personal.IdNumber || personal.idNumber || 'N/A';
                const gender = app.MasterGender || personal.Gender || personal.gender || 'N/A';
                const race = app.MasterRace || personal.Race || personal.race || 'N/A';

                const guardianName = guardianDb.FirstName || `${guardianJson.firstNames || guardianJson.firstName || ''} ${guardianJson.surname || ''}`.trim() || 'N/A';
                const guardianRelation = guardianDb.Relation || guardianJson.relation || 'N/A';

                return {
                    "Application Ref": app.ApplicationRef || `A${app.Id}`,
                    "Bursary Fund": app.FundName || 'N/A',
                    "Status": app.Status || 'Draft',
                    "Status Reason / Funder Feedback": statusReason.trim(),
                    "Submission Date": app.SubmittedAt ? new Date(app.SubmittedAt).toLocaleDateString() : 'Draft',
                    
                    "First Names": firstNames,
                    "Surname": surname,
                    "Full Name": app.FullName || `${firstNames} ${surname}`.trim(),
                    "Email Address": app.Email || 'N/A',
                    "Phone Number": cellNumber,
                    "ID Number": idNumber,
                    "Gender": gender,
                    "Race / Ethnicity": race,
                    "Citizenship": app.MasterCitizenship || personal.citizenship || 'South Africa',
                    "T-Shirt Size": personal.tShirtSize || 'N/A',

                    "Street Address": [address.physicalUnit, address.physicalStreet].filter(Boolean).join(', ') || 'N/A',
                    "City / Town": address.physicalTown || address.physicalCity || 'N/A',
                    "Province": address.physicalProvince || app.Province || 'N/A',
                    "Postal Code": address.physicalCode || 'N/A',

                    "Current Academic Status": academic.currentStatus || 'N/A',
                    "Intended Level of Study": academic.intendedLevel || academic.currentLevel || 'N/A',
                    "Institution (Choice 1)": academic.intendedInstitution1 || academic.intendedInstitution || 'N/A',
                    "Course / Field (Choice 1)": academic.intendedCourse1 || academic.intendedCourse || 'N/A',
                    "High School Subjects": subjectsSummary,

                    "Household Income": isNaN(financial.householdIncome) 
                        ? (financial.householdIncome || 'N/A') 
                        : `R ${parseFloat(financial.householdIncome || 0).toLocaleString('en-ZA')}`,
                    "Number of Dependants": financial.numberOfDependants || 'N/A',
                    "Needs Accommodation": financial.needsAccommodation || 'N/A',
                    "Other Funding / Aid": financial.otherAid || 'N/A',

                    "Guardian Name": guardianName,
                    "Guardian Relationship": guardianRelation,
                    "NUM Member Status": jbmarks.numStatus || 'N/A',
                    "NUM Region / Branch": [jbmarks.region, jbmarks.branch].filter(Boolean).join(' / ') || 'N/A',
                    "Employer / Company Name": jbmarks.companyName || guardianDb.Employer || guardianJson.employer || 'N/A',
                    "Industry Sector": guardianJson.industry || 'N/A',
                    "Industry Number": jbmarks.industryNo || jbmarks.industryNumber || 'N/A',

                    "AI Vibe Check Profile": app.archetype || 'Pending'
                };
            });

            const worksheet = XLSX.utils.json_to_sheet(exportData);

            const columnWidths = Object.keys(exportData[0]).map(key => {
                let maxLen = key.length;
                exportData.forEach(row => {
                    const val = String(row[key] || '');
                    if (val.length > maxLen) maxLen = val.length;
                });
                return { wch: Math.min(Math.max(maxLen + 5, 15), 55) };
            });

            worksheet['!cols'] = columnWidths;

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Applications");

            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const dataBlob = new Blob([new Uint8Array(excelBuffer)], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });

            const timestamp = new Date().toISOString().split('T')[0];
            saveAs(dataBlob, `BrightSparc_Applications_Report_${timestamp}.xlsx`);

            showToast(`Exported ${exportData.length} record(s) to Excel successfully.`, "success");

        } catch (err) {
            console.error("Export Error:", err);
            showToast("Failed to generate complete Excel export.", "error");
        }
    };

    const columns = [
        { 
            field: 'ApplicationRef', 
            headerName: 'Ref Number', 
            width: 140, 
            valueGetter: (params) => params.row?.ApplicationRef || `A${params.row?.Id}` 
        },
        { 
            field: 'ApplicantName', 
            headerName: 'Applicant Name', 
            flex: 1, 
            minWidth: 180,
            valueGetter: (params) => params.row?.FullName || `${params.row?.FirstNames || ''} ${params.row?.Surname || ''}`.trim() || 'Unknown'
        },
        { field: 'Email', headerName: 'Email', width: 200 },
        { field: 'FundName', headerName: 'Bursary Fund', width: 200 },
        { 
            field: 'Status', 
            headerName: 'Status', 
            width: 140,
            renderCell: (params) => (
                <Chip 
                    label={params.value} 
                    size="small" 
                    color={params.value === 'Submitted' ? 'info' : params.value === 'Approved' ? 'success' : params.value === 'Rejected' ? 'error' : 'default'} 
                    variant="outlined" 
                />
            )
        },
        {
            field: 'Feedback',
            headerName: 'Rejection Reason / Feedback',
            flex: 1.5,
            minWidth: 220,
            renderCell: (params) => {
                const feedback = params.row?.Feedback;
                const isRejected = params.row?.Status === 'Rejected';

                if (feedback) {
                    return (
                        <Box sx={{ py: 0.5, width: '100%' }}>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: isRejected ? 'error.main' : 'text.primary',
                                    fontWeight: isRejected ? 'medium' : 'normal',
                                    lineHeight: 1.2
                                }}
                                title={feedback}
                            >
                                {feedback}
                            </Typography>
                        </Box>
                    );
                }

                if (isRejected) {
                    return (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No reason logged
                        </Typography>
                    );
                }

                return <Typography variant="caption" color="text.disabled">—</Typography>;
            }
        },
        {
            field: 'DocCount',
            headerName: 'Docs & Status',
            width: 170,
            renderCell: (params) => {
                const hasRequested = !!params.row.RequestedDocuments;
                const resubmitted = params.row.DocsResubmitted === 1;

                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AttachFileIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight="bold">{params.row.DocCount || 0}</Typography>
                        </Box>
                        {resubmitted ? (
                            <Chip 
                                label="Updated Docs" 
                                size="small" 
                                color="success" 
                                icon={<CheckCircleOutlineIcon />} 
                                sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                        ) : hasRequested ? (
                            <Chip 
                                label="Docs Requested" 
                                size="small" 
                                color="warning" 
                                variant="outlined"
                                icon={<WarningAmberIcon />} 
                                sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                        ) : null}
                    </Box>
                );
            }
        },
        { 
            field: 'SubmittedAt', 
            headerName: 'Submitted', 
            width: 150,
            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'Draft'
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 280,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Button 
                        component={RouterLink} 
                        to={`/admin/review/${params.row.Id}`} 
                        variant="contained" 
                        size="small" 
                        startIcon={<AssignmentTurnedInIcon />}
                    >
                        Review
                    </Button>
                    {tenantConfig.features?.enableDocRequests && ['Admin', 'SuperUser', 'Reviewer'].includes(user.role) && (
                        <Button 
                            size="small" 
                            variant="outlined" 
                            color="warning"
                            onClick={() => {
                                setSelectedApp(params.row);
                                setIsRequestDocsOpen(true);
                            }}
                        >
                            Request Docs
                        </Button>
                    )}
                </Box>
            )
        },
        { 
            field: 'archetype', 
            headerName: 'AI Career Profile', 
            width: 200, 
            renderCell: (params) => {
                if (params.value) {
                    return (
                        <Chip 
                            icon={<AutoAwesomeIcon sx={{ color: '#ffeb3b !important' }} />} 
                            label={params.value} 
                            sx={{ 
                                background: 'linear-gradient(135deg, #4a148c 0%, #7b1fa2 100%)', 
                                color: 'white', 
                                fontWeight: 'bold' 
                            }} 
                            size="small" 
                        />
                    );
                }
                return <Typography variant="caption" color="text.secondary">Pending Vibe Check</Typography>;
            } 
        }
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
             <Typography variant="h4" fontWeight="bold" gutterBottom>Application Search</Typography>
             
             <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField fullWidth size="small" label="Search Name/Email" value={searchText} onChange={e => setSearchText(e.target.value)} InputProps={{ endAdornment: <SearchIcon /> }} />
                        {searchParams.get('filter') === 'assigned_to_me' && (
                            <Chip 
                                label="Viewing My Assigned Tasks" 
                                color="primary" 
                                size="small" 
                                onDelete={() => {
                                    const newParams = new URLSearchParams(searchParams);
                                    newParams.delete('filter');
                                    setSearchParams(newParams);
                                }} 
                                sx={{ mt: 1 }}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select value={currentStatus} label="Status" onChange={handleStatusFilterChange}>
                                <MenuItem value="All">All Statuses</MenuItem>
                                <MenuItem value="Draft">Draft</MenuItem>
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Submitted">Submitted</MenuItem>                                
                                <MenuItem value="Under Review">Under Review</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Shortlisted">Shortlisted</MenuItem>
                                <MenuItem value="Interview">Interview</MenuItem>
                                <MenuItem value="Rejected">Rejected</MenuItem>
                                <MenuItem value="Funded">Funded</MenuItem>
                                <MenuItem value="Withdrawn">Withdrawn</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={7} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                         <Button 
                            variant="outlined" 
                            color="secondary" 
                            startIcon={<EmailIcon />}
                            disabled={selectionModel.length === 0}
                            onClick={() => setIsBulkEmailOpen(true)}
                        >
                            Email ({selectionModel.length})
                        </Button>
                        <Button 
                            variant="outlined" 
                            color="primary" 
                            startIcon={<RuleIcon />}
                            disabled={selectionModel.length === 0}
                            onClick={() => setIsBulkStatusOpen(true)}
                        >
                            Status ({selectionModel.length})
                        </Button>
                        <Button 
                            variant="contained" 
                            color="info" 
                            startIcon={<FileDownloadIcon />}
                            disabled={selectionModel.length === 0}
                            onClick={handleExcelExport}
                        >
                            Export Excel
                        </Button>
                        <Button 
                            variant="contained" 
                            color="success" 
                            startIcon={<AccountBalanceIcon />}
                            onClick={handleBankExport}
                        >
                            Bank Batch
                        </Button>
                    </Grid>
                </Grid>
             </Paper>

             {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

             <Card>
                <Box sx={{ height: 600, width: '100%' }}>
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        getRowId={(row) => row.Id}
                        loading={loading}
                        checkboxSelection
                        onRowSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                        rowSelectionModel={selectionModel}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                        pageSizeOptions={[10, 25, 50]}
                    />
                </Box>
             </Card>

            {selectedApp && (
                <AssignReviewerModal 
                    open={isAssignModalOpen} 
                    onClose={handleCloseModals} 
                    application={selectedApp} 
                    token={token} 
                    onAssign={fetchApplications} 
                    showToast={showToast} 
                />
            )}

            {selectedApp && (
                <RequestDocumentsModal 
                    open={isRequestDocsOpen} 
                    onClose={handleCloseModals} 
                    application={selectedApp} 
                    token={token} 
                    onUpdated={fetchApplications} 
                    showToast={showToast} 
                />
            )}

            <BulkStatusUpdateModal 
                open={isBulkStatusOpen} 
                onClose={handleCloseModals} 
                selectedIds={selectionModel} 
                token={token} 
                onUpdate={() => { fetchApplications(); setSelectionModel([]); }} 
                showToast={showToast} 
            />

            <BulkSendEmailModal 
                open={isBulkEmailOpen} 
                onClose={handleCloseModals} 
                selectedIds={selectionModel} 
                token={token} 
                onUpdate={() => { fetchApplications(); setSelectionModel([]); }} 
                showToast={showToast} 
            />
            
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })} 
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default SearchApplicationsPage;
